import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api from "../services/api";

const TicketDetail = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  console.log("id", id);
  const [loading, setLoading] = useState(true);
  const [ticket, setTicket] = useState(null);
  const [messages, setMessages] = useState([]);
  const [attachments, setAttachments] = useState([]);
  const [replyMessage, setReplyMessage] = useState("");
  const [replying, setReplying] = useState(false);
  const [assigning, setAssigning] = useState(false);
  const [users, setUsers] = useState([]);
  const [showAssignForm, setShowAssignForm] = useState(false);
  const [assignData, setAssignData] = useState({
    assigned_to_user_id: "",
    assigned_to_role: "admin",
    notes: "",
  });

  useEffect(() => {
    fetchTicketDetails();
  }, [id]);

  const fetchTicketDetails = async () => {
    if (!id) {
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const response = await api.get(`/tickets/${id}`);
      if (response.data?.success) {
        setTicket(response.data.data.ticket);
        setMessages(response.data.data.messages || []);
        setAttachments(response.data.data.attachments || []);
      }
    } catch (error) {
      console.error("Error fetching ticket:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await api.get("/users", { params: { limit: 100 } });
      if (response.data?.data) {
        setUsers(response.data.data);
      }
    } catch (error) {
      console.error("Error fetching users:", error);
    }
  };

  const handleReply = async (e) => {
    e.preventDefault();
    if (!replyMessage.trim()) {
      alert("Please enter a message");
      return;
    }

    setReplying(true);
    const formData = new FormData();
    formData.append("message", replyMessage);

    const fileInput = document.getElementById("reply-attachments");
    if (fileInput && fileInput.files.length > 0) {
      Array.from(fileInput.files).forEach((file) => {
        formData.append("attachments", file);
      });
    }

    try {
      const response = await api.post(`/tickets/${id}/reply`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      if (response.data?.success) {
        alert("Reply added successfully!");
        setReplyMessage("");
        if (fileInput) fileInput.value = "";
        fetchTicketDetails();
      } else {
        alert(response.data?.message || "Failed to add reply");
      }
    } catch (error) {
      console.error("Error replying to ticket:", error);
      alert("Failed to add reply");
    } finally {
      setReplying(false);
    }
  };

  const handleAssign = async (e) => {
    e.preventDefault();
    if (!assignData.assigned_to_user_id) {
      alert("Please select a user to assign");
      return;
    }

    setAssigning(true);
    try {
      const response = await api.post(`/tickets/${id}/assign`, assignData);
      if (response.data?.success) {
        alert("Ticket assigned successfully!");
        setShowAssignForm(false);
        setAssignData({ assigned_to_user_id: "", assigned_to_role: "admin", notes: "" });
        fetchTicketDetails();
      } else {
        alert(response.data?.message || "Failed to assign ticket");
      }
    } catch (error) {
      console.error("Error assigning ticket:", error);
      alert("Failed to assign ticket");
    } finally {
      setAssigning(false);
    }
  };

  const handleStatusChange = async (newStatus) => {
    try {
      const response = await api.put(`/tickets/${id}/status`, { status: newStatus });
      if (response.data?.success) {
        alert("Ticket status updated!");
        fetchTicketDetails();
      } else {
        alert(response.data?.message || "Failed to update status");
      }
    } catch (error) {
      console.error("Error updating status:", error);
      alert("Failed to update status");
    }
  };

  const getStatusBadge = (status) => {
    const variants = {
      Open: "bg-blue-100 text-blue-800",
      Answered: "bg-blue-100 text-blue-800",
      "In Progress": "bg-yellow-100 text-yellow-800",
      Closed: "bg-gray-100 text-gray-800",
      Reopened: "bg-purple-100 text-purple-800",
    };
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-semibold ${variants[status] || "bg-gray-100"}`}>
        {status}
      </span>
    );
  };

  const getUrgencyBadge = (urgency) => {
    const variants = {
      Low: "bg-blue-100 text-blue-800",
      Medium: "bg-yellow-100 text-yellow-800",
      High: "bg-red-100 text-red-800",
    };
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-semibold ${variants[urgency] || "bg-gray-100"}`}>
        {urgency}
      </span>
    );
  };

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    return date.toLocaleString("en-IN", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  if (loading) {
    return (
      <div className="p-6 text-center">
        <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-700"></div>
      </div>
    );
  }

  if (!ticket) {
    return (
      <div className="p-6">
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">Ticket not found</div>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Ticket Header */}
      <div className="bg-white rounded-lg shadow mb-6 p-6">
        <div className="flex justify-between items-start mb-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-800 mb-2">Ticket #{ticket.ticket_number}</h1>
            <p className="text-gray-500">Created: {formatDate(ticket.created_at)}</p>
          </div>
          <div className="flex gap-2">
            {getStatusBadge(ticket.status)} {getUrgencyBadge(ticket.urgency)}
          </div>
        </div>

        <div className="mb-4">
          <h2 className="text-xl font-semibold text-gray-800 mb-2">{ticket.subject}</h2>
          <div className="text-gray-600 space-y-1">
            <p>
              <strong>Department:</strong> {ticket.department_name || "-"}
            </p>
            {ticket.assigned_to_user_id && (
              <p>
                <strong>Assigned to:</strong>{" "}
                {ticket.assigned_firstname && ticket.assigned_lastname
                  ? `${ticket.assigned_firstname} ${ticket.assigned_lastname}`
                  : "User"}
              </p>
            )}
          </div>
        </div>

        <div className="flex gap-2">
          <button
            onClick={() => navigate("/superadmin/tickets")}
            className="bg-gray-600 hover:bg-gray-700 text-white px-4 py-2 rounded-md"
          >
            Back to List
          </button>
          {(ticket.status !== "Closed" || ticket.created_by_user_id) && (
            <button
              onClick={() => handleStatusChange(ticket.status === "Closed" ? "Reopened" : "Closed")}
              className={`px-4 py-2 rounded-md ${
                ticket.status === "Closed"
                  ? "bg-blue-600 hover:bg-blue-700"
                  : "bg-red-600 hover:bg-red-700"
              } text-white`}
            >
              {ticket.status === "Closed" ? "Reopen" : "Close"} Ticket
            </button>
          )}
        </div>
      </div>

      {/* Messages/Replies */}
      <div className="bg-white rounded-lg shadow mb-6 p-6">
        <h3 className="text-xl font-semibold mb-4">Conversation</h3>
        <div className="space-y-4">
          {messages.map((message, index) => (
            <div
              key={message.id}
              className={`p-4 rounded-lg ${index % 2 === 0 ? "bg-gray-50" : "bg-white"} border border-gray-200`}
            >
              <div className="flex justify-between items-center mb-2">
                <div className="flex items-center gap-2">
                  <strong>
                    {message.firstname && message.lastname
                      ? `${message.firstname} ${message.lastname}`
                      : "User"}
                  </strong>
                  <span className="px-2 py-1 bg-gray-200 text-gray-700 rounded text-xs">
                    {message.replied_by_role}
                  </span>
                </div>
                <small className="text-gray-500">{formatDate(message.created_at)}</small>
              </div>
              <div className="text-gray-700 whitespace-pre-wrap">{message.message}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Attachments */}
      {attachments.length > 0 && (
        <div className="bg-white rounded-lg shadow mb-6 p-6">
          <h3 className="text-xl font-semibold mb-4">Attachments</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {attachments.map((att) => (
              <a
                key={att.id}
                href={att.file_url}
                target="_blank"
                rel="noopener noreferrer"
                className="block p-3 border border-gray-200 rounded-lg hover:bg-gray-50"
              >
                <div className="font-medium text-gray-800">{att.original_filename}</div>
                <div className="text-sm text-gray-500">{(att.file_size / 1024).toFixed(2)} KB</div>
              </a>
            ))}
          </div>
        </div>
      )}

      {/* Reply Form */}
      {ticket.status !== "Closed" && (
        <div className="bg-white rounded-lg shadow mb-6 p-6">
          <h3 className="text-xl font-semibold mb-4">Add Reply</h3>
          <form onSubmit={handleReply}>
            <div className="mb-4">
              <textarea
                rows={4}
                value={replyMessage}
                onChange={(e) => setReplyMessage(e.target.value)}
                placeholder="Type your reply here..."
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">Attachments (Optional)</label>
              <input
                id="reply-attachments"
                type="file"
                multiple
                accept=".png,.jpg,.jpeg,.pdf,.doc,.xls,.xlsx"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <p className="text-xs text-gray-500 mt-1">
                Allowed: png, jpg, jpeg, pdf, doc, xls, xlsx (Max 10MB per file)
              </p>
            </div>
            <button
              type="submit"
              disabled={replying}
              className="bg-blue-700 hover:bg-blue-800 text-white px-6 py-2 rounded-md font-semibold disabled:opacity-50"
            >
              {replying ? "Sending..." : "Send Reply"}
            </button>
          </form>
        </div>
      )}

      {/* Assignment Form (Admin Only) */}
      {showAssignForm && (
        <div className="bg-white rounded-lg shadow mb-6 p-6">
          <h3 className="text-xl font-semibold mb-4">Assign Ticket</h3>
          <form onSubmit={handleAssign}>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">Assign to User</label>
              <select
                value={assignData.assigned_to_user_id}
                onChange={(e) => {
                  setAssignData({ ...assignData, assigned_to_user_id: e.target.value });
                  if (!users.length) fetchUsers();
                }}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              >
                <option value="">Select User</option>
                {users.map((user) => (
                  <option key={user.id} value={user.id}>
                    {user.firstname} {user.lastname} ({user.email || user.mobile})
                  </option>
                ))}
              </select>
            </div>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">Role</label>
              <select
                value={assignData.assigned_to_role}
                onChange={(e) => setAssignData({ ...assignData, assigned_to_role: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="admin">Admin</option>
                <option value="support_agent">Support Agent</option>
              </select>
            </div>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">Notes (Optional)</label>
              <textarea
                rows={2}
                value={assignData.notes}
                onChange={(e) => setAssignData({ ...assignData, notes: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div className="flex gap-2">
              <button
                type="submit"
                disabled={assigning}
                className="bg-blue-700 hover:bg-blue-800 text-white px-6 py-2 rounded-md font-semibold disabled:opacity-50"
              >
                {assigning ? "Assigning..." : "Assign"}
              </button>
              <button
                type="button"
                onClick={() => {
                  setShowAssignForm(false);
                  setAssignData({ assigned_to_user_id: "", assigned_to_role: "admin", notes: "" });
                }}
                className="bg-gray-600 hover:bg-gray-700 text-white px-6 py-2 rounded-md font-semibold"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Admin Actions */}
      {!showAssignForm && (
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex gap-2">
            {/* <button
              onClick={() => {
                setShowAssignForm(true);
                if (!users.length) fetchUsers();
              }}
              className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md"
            >
              Assign Ticket
            </button> */}
            {ticket.status !== "In Progress" && (
              <button
                onClick={() => handleStatusChange("In Progress")}
                className="bg-yellow-600 hover:bg-yellow-700 text-white px-4 py-2 rounded-md"
              >
                Mark as In Progress
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default TicketDetail;

